import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  ScaleFade,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  Flex,
  Text,
  Box,
  Img,
  InputGroup,
  Input,
  FormLabel,
  InputRightElement,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faShareFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { faClipboard } from '@fortawesome/pro-light-svg-icons';

import { isDev } from 'utils';
import FontAwesomeIcon from 'components/primatives/FontAwesomeIcon';
import useLeadShareLink from 'hooks/useLeadShareLink';
import ModalHeader from 'components/primatives/ModalHeader';

const Body = ({
  data,
}: {
  data: ReturnType<typeof useLeadShareLink>['data'];
}) => {
  const { t } = useTranslation();
  const toast = useToast();

  const handleCopy = () => {
    navigator.clipboard.writeText(data?.url || '');
    toast({
      title: t('Copied to clipboard'),
      status: 'success',
      isClosable: true,
    });
  };

  return (
    <>
      {data && (
        <Flex direction="column" align="center" gap={4}>
          <Box>
            <Text>
              <Trans>
                Scan this QR code or share this link to work on your request
                from another device.
              </Trans>{' '}
              <Trans>
                Your updates are saved and accessible between devices.
              </Trans>
            </Text>
          </Box>
          <Img src={data.qr} alt={t('QR Code')} />
          <InputGroup>
            <Input
              variant="fancy"
              className={clsx('shrink-label')}
              onClick={(event) => event.currentTarget.select()}
              value={data.url}
              readOnly
            />
            <FormLabel>
              <Trans>Link</Trans>
            </FormLabel>
            <InputRightElement p={2}>
              <IconButton
                variant="unstyled"
                aria-label={t('Copy share link')}
                onClick={handleCopy}
              >
                <FontAwesomeIcon icon={faClipboard} />
              </IconButton>
            </InputRightElement>
          </InputGroup>
        </Flex>
      )}
    </>
  );
};

const ShareButton = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, isFetched } = useLeadShareLink();
  return (
    <Box
      position="fixed"
      bottom={2}
      left={isDev ? 2 : undefined}
      right={isDev ? undefined : 2}
      zIndex="10000"
    >
      {!isOpen && isFetched && (
        <ScaleFade initialScale={0.5} in={true}>
          <IconButton
            onClick={onOpen}
            variant="ghost"
            border="1px solid var(--chakra-colors-tmtGray05)"
            bgColor="bodyBgColor"
            _hover={{
              bgColor: 'tmtGold',
              color: 'bodyFontColor',
            }}
            _dark={{
              bgColor: 'darkBodyBgColor',
              color: 'white',
              border: '1px solid var(--chakra-colors-tmtGray55)',
              _hover: {
                bgColor: 'tmtGold',
                color: 'bodyFontColor',
              },
            }}
            aria-label={t('Share')}
            borderRadius="full"
            minW={0}
            height="auto"
            p={2}
            boxShadow="0px 0px 5px var(--chakra-colors-tmtGray55)"
            fontSize="xs"
          >
            <FontAwesomeIcon icon={faShareFromSquare} />
          </IconButton>
        </ScaleFade>
      )}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          onClose && onClose();
        }}
      >
        <ModalOverlay>
          <ModalContent>
            <ModalHeader title={<Trans>Share</Trans>} />
            <ModalBody>
              <Body data={data} />
            </ModalBody>
            <ModalFooter>
              <Button
                onClick={onClose}
                leftIcon={<FontAwesomeIcon icon={faCheck} />}
                minW="25%"
              >
                <Trans>Got it</Trans>
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </Box>
  );
};

export default ShareButton;
